import type { LabelInValue, UploadedFile } from 'models';
import { type RefObject, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import { Button, Col, Flex, Form, Icon, type RichTextEditorBase, RichTextInput, Row, Text, message } from 'ui';
import { useUser } from 'features/auth';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Break circular dependency
import { type CommunityLabelInValue, CommunitySelectWithUserProfilePhoto } from 'features/communities/components';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- Break circular dependency
import { useCommunityMentions } from 'features/communities/hooks';
import { CreatePoll, type PollSubmitData } from 'features/polls';
import { AttachmentInput } from '../comment-form';
import { FileAttachments } from '../comment-form/file-attachments';
import { ExpressingKudo } from '../expressing-kudo';
import { PinnedPostToggle } from '../pinned-post-toggle';
import { useGetInitialValues as usePostFormInitialValues } from '../post-form/hooks';
import { useCreateKudoMutation, useCreatePollMutation, useCreatePostMutation } from '../queries';
import { type CreateCommunityOptionSource, createCommunityOption } from '../utils';
import { CreatePostTriggerPlaceholder } from './create-post-trigger-placeholder';
import { useAttachmentsRules, useCommunityRules, useHtmlContentRules } from './hooks';

type FormValues = {
  attachments: { files: UploadedFile[]; loading: boolean };
  community?: CommunityLabelInValue;
  htmlContent: string;
  pinned: boolean;
  recipient: LabelInValue | null;
  textContent: string;
};

type P = {
  editorRef?: RefObject<RichTextEditorBase>;
  groupId?: string;
  initialCommunity?: CreateCommunityOptionSource;
  initialMode?: PostMode;
};

type PostMode = 'input' | 'placeholder';

// eslint-disable-next-line sonarjs/cognitive-complexity, complexity -- TODO: needs to be rewrite
const CreateContentObjectTrigger = ({ editorRef, groupId, initialCommunity, initialMode = 'placeholder' }: P) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const [mode, setMode] = useState<PostMode>(initialMode);
  const [poll, setPoll] = useState<PollSubmitData>();
  const [isCreatePollOpen, setIsCreatePollOpen] = useState(false);
  const [isKudoPickerOpen, setIsKudoPickerOpen] = useState(false);

  const { railsUserData: viewer } = useUser();

  const formInitialValues = {
    ...usePostFormInitialValues(),
    community: initialCommunity ? createCommunityOption(initialCommunity) : undefined,
  };
  const { canManageSelectedCommunity } = usePermissions();
  const attachments = Form.useWatch<FormValues['attachments'] | undefined>('attachments', form) ?? {
    files: [],
    loading: false,
  };

  const community = Form.useWatch<FormValues['community']>('community', form);
  const communityRules = useCommunityRules();
  const htmlContentRules = useHtmlContentRules();
  const attachmentsRules = useAttachmentsRules();
  const getCommunityId = useCallback(() => form.getFieldsValue().community?.value, [form]);
  const handleOnRequestMentions = useCommunityMentions(getCommunityId);

  const { isPending: isCreatingPost, mutate: createPost } = useCreatePostMutation();
  const { isPending: isCreatingKudo, mutate: createKudo } = useCreateKudoMutation();
  const { isPending: isCreatingPoll, mutate: createPoll } = useCreatePollMutation();

  const type = groupId ? 'announcement' : 'post';
  const isLoading = isCreatingPost || isCreatingKudo || isCreatingPoll;

  const label = type === 'announcement' ? 'Announcement' : 'Post';
  const isPollButtonVisible = type === 'post';

  const resetForm = () => {
    form.resetFields();
    setPoll(undefined);
    setIsKudoPickerOpen(false);
    setMode('placeholder');
  };

  const onFinish = (values: FormValues) => {
    if (isKudoPickerOpen && !values.recipient) {
      message.error('Please, choose who to give kudos to.');
      return;
    }

    if (poll) {
      createPoll(
        {
          attachments: values.attachments.files,
          communityId: values.community?.value,
          htmlContent: values.htmlContent,
          pinned: values.pinned,
          poll,
          textContent: values.textContent,
        },
        {
          onSuccess() {
            message.success(t('Your {{content}} has been published', { content: t('poll') }));
            resetForm();
          },
        },
      );
      return;
    }

    if (values.recipient) {
      createKudo(
        {
          attachments: values.attachments.files,
          communityId: values.community?.value ?? '',
          htmlContent: values.htmlContent,
          pinned: values.pinned,
          textContent: values.textContent,
          userId: values.recipient.value,
        },
        {
          onSuccess() {
            message.success(t('Your {{content}} has been published', { content: t('kudos') }));
            resetForm();
          },
        },
      );
      return;
    }

    createPost(
      {
        attachments: values.attachments.files,
        communityId: values.community?.value,
        groupId,
        htmlContent: values.htmlContent,
        pinned: values.pinned,
        textContent: values.textContent,
      },
      {
        onSuccess() {
          message.success(t('Your {{content}} has been published', { content: t(type) }));
          resetForm();
        },
      },
    );
  };

  if (mode === 'placeholder') {
    return (
      <CreatePostTriggerPlaceholder
        onRequestInput={() => {
          setMode('input');
        }}
        type={type}
        viewer={viewer}
      />
    );
  }

  return (
    <>
      <Form<FormValues> form={form} initialValues={formInitialValues} onFinish={onFinish}>
        <Row>
          {type === 'post' ? (
            <Col span={24}>
              <Form.Item<FormValues> name="community" rules={communityRules}>
                <CommunitySelectWithUserProfilePhoto />
              </Form.Item>
            </Col>
          ) : null}

          <Col span={24}>
            <RichTextInput
              html={{
                className: 'post-form__editor',
                dependencies: ['attachments'],
                editorRef,
                isFocusedInitially: true,
                label,
                mentions: handleOnRequestMentions,
                name: 'htmlContent',
                noOptional: true,
                placeholder: 'Write content...',
                rules: htmlContentRules,
              }}
              text={{ name: 'textContent' }}
            />
          </Col>

          {type === 'post' && poll ? (
            <Col span={24}>
              <Flex align="center" className="post-form__poll-body" gap="xxs" justify="space-between">
                <Flex
                  flex={1}
                  gap="xs"
                  onClick={() => {
                    setIsCreatePollOpen(true);
                  }}
                >
                  <Icon icon="list-check-3-line" />
                  <Text>Poll</Text>
                </Flex>
                <Button
                  aria-label="Delete poll"
                  data-test-id="btn_delete-poll"
                  icon={<Icon icon="close-line" />}
                  onClick={() => {
                    setPoll(undefined);
                  }}
                  size="small"
                  type="text"
                />
              </Flex>
            </Col>
          ) : null}

          {attachments.files.length > 0 ? (
            <Col className="post-form__attachments" span={24}>
              <FileAttachments
                attachments={attachments.files}
                onClear={() => {
                  form.setFieldsValue({ attachments: { files: [], loading: attachments.loading } });
                }}
                onRemove={(attachment) => {
                  form.setFieldsValue({
                    attachments: {
                      files: attachments.files.filter((file) => file !== attachment),
                      loading: attachments.loading,
                    },
                  });
                }}
              />
            </Col>
          ) : null}

          {type === 'post' && !poll && isKudoPickerOpen ? (
            <Col span={24}>
              <Row align="top" gutter="ms">
                <Col flex="auto">
                  <Form.Item<FormValues> label="Expressing Kudos to:" layout="horizontal" name="recipient" noOptional>
                    <ExpressingKudo communityIds={community ? [community.value] : []} />
                  </Form.Item>
                </Col>
                <Col alignItems="center">
                  <Button
                    icon={<Icon icon="close-circle-line" />}
                    onClick={() => {
                      setIsKudoPickerOpen(false);
                      form.setFieldValue('recipient', null);
                    }}
                  />
                </Col>
              </Row>
            </Col>
          ) : null}

          <Col span={24}>
            <Row align="middle" justify="space-between">
              <Col>
                <Row align="middle" gutter={[0, 'ms']}>
                  {isPollButtonVisible ? (
                    <Col>
                      <Button
                        aria-label="Create poll"
                        disabled={isKudoPickerOpen}
                        icon={<Icon icon="list-check-3-line" />}
                        onClick={() => {
                          setIsCreatePollOpen(true);
                        }}
                        type="text"
                      />
                    </Col>
                  ) : null}

                  <Col>
                    <Form.Item<FormValues> name="attachments" noStyle rules={attachmentsRules}>
                      <AttachmentInput />
                    </Form.Item>
                  </Col>

                  {type === 'post' ? (
                    <Col>
                      <Button
                        aria-label="Create kudo"
                        disabled={Boolean(poll)}
                        icon={<Icon icon="medal-line" />}
                        onClick={() => {
                          setIsKudoPickerOpen(true);
                        }}
                        type="text"
                      />
                    </Col>
                  ) : null}
                </Row>
              </Col>
              <Col>
                <Row align="middle" gutter={['lg', 'lg']}>
                  {type === 'post' && canManageSelectedCommunity({ community }) ? (
                    <Col>
                      <Form.Item<FormValues> name="pinned" noStyle>
                        <PinnedPostToggle />
                      </Form.Item>
                    </Col>
                  ) : null}
                  <Col>
                    <Button htmlType="submit" icon={<Icon icon="send-line" />} loading={isLoading} type="primary">
                      Post
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>

      <CreatePoll
        isOpened={isCreatePollOpen}
        onCancel={() => {
          setIsCreatePollOpen(false);
        }}
        onSuccess={(values: PollSubmitData) => {
          setPoll(values);
          setIsCreatePollOpen(false);
        }}
      />
    </>
  );
};

export default CreateContentObjectTrigger;
